import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Button,
  Tabs,
  Tab,
  Avatar,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import dayjs from "dayjs";
import interviewIcon from "../../assets/images/mental.png";
import examTime from "../../assets/images/exam-time.png";
import exam from "../../assets/images/exam.png";
import test from "../../assets/images/test.png";
import {
  cardSx,
  commonAvatarStyle,
  initials,
  SectionTitle,
  KpiCard,
  InterviewsByStatus,
  UpcomingInterviews,
  MONTH_OPTIONS,
  DashboardHeader,
  useDashboardSummary,
  useUpcomingInterviews,
  showApiError,
} from "./CorporateDashboard";
import { TableNodata } from "../../components/TableNodata";
import { TruncatedText } from "../../components/TruncatedText";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";

/**
 * Corporate Interviewer dashboard — same building blocks and style as CorporateDashboard.
 *
 * APIs (all through the shared api client, src/api/client.js):
 *   GET /dashboard/summary               KPI cards, all-time figures           (useDashboardSummary(null), CorporateDashboard)
 *   GET /dashboard/summary?year=&month=  Interviews by Status, selected month  (useDashboardSummary(month), CorporateDashboard)
 *   GET /upcoming-interviews             Upcoming Interviews list            (useUpcomingInterviews, CorporateDashboard)
 *   GET /pending-feedback                Feedback Ledger · Pending tab       (useFeedbackList, below)
 *   GET /completed-feedback              Feedback Ledger · Completed tab     (useFeedbackList, below)
 */

// Upcoming list: interviews still to happen (scheduled or rescheduled)
const ACTIVE_STATUSES = ["SCHEDULED", "RESCHEDULED"];

// "3 days ago" / "5 hours ago" for the conducted time
const timeAgo = (value) => {
  const at = dayjs(value);
  if (!at.isValid()) return "";
  const hours = dayjs().diff(at, "hour");
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = dayjs().diff(at, "day");
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

// Feedback Ledger shows exactly 5 rows; more rows scroll under the sticky header. Same height in both tabs.
const LEDGER_HEADER_HEIGHT = 40;
const LEDGER_ROW_HEIGHT = 62;
const LEDGER_VISIBLE_ROWS = 5;
const LEDGER_HEIGHT = LEDGER_HEADER_HEIGHT + LEDGER_ROW_HEIGHT * LEDGER_VISIBLE_ROWS + 2; // + container border

/* ---------------- Building blocks ---------------- */

// Date + "x days ago" cell
const DateCell = ({ value }) => {
  const at = dayjs(value);
  return (
    <TableCell sx={{ whiteSpace: "nowrap" }}>
      <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{at.isValid() ? at.format("DD MMM YYYY, hh:mm A") : "-"}</Typography>
      <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>{timeAgo(value)}</Typography>
    </TableCell>
  );
};

// Compact month filter for the Interviews by Status card header (last 12 months)
const MonthFilter = ({ value, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 130 }}>
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      MenuProps={{
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        transformOrigin: { vertical: "top", horizontal: "right" },
        slotProps: { paper: { sx: { mt: 0.5 } } },
      }}
      sx={{ fontSize: "12px", height: 32, "& .MuiSelect-select": { py: 0.5, pl: 1.25 } }}
    >
      {MONTH_OPTIONS.map((month) => (
        <MenuItem key={month.format("YYYY-MM")} value={month.format("YYYY-MM")} sx={{ fontSize: "12px" }}>
          {month.format("MMMM YYYY")}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/* ---------------- Sections ---------------- */

// Pending: Feedback due + Submit feedback. Completed: Feedback submitted date + View feedback.
const FeedbackTable = ({ data, loading, completed, onOpen }) => {
  if (loading) {
    return (
      <Box sx={{ height: LEDGER_HEIGHT }}>
        {Array.from({ length: LEDGER_VISIBLE_ROWS }, (_, row) => (
          <Skeleton key={row} variant="rounded" height={LEDGER_ROW_HEIGHT - 8} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }
  if (data.length === 0) {
    return (
      <Box sx={{ height: LEDGER_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {completed ? (
          <TableNodata message="No Completed Feedback" description="Feedback you have submitted appears here." />
        ) : (
          <TableNodata message="No Pending Feedback" description="You're all caught up. Interviews waiting for your feedback appear here." />
        )}
      </Box>
    );
  }
  const columns = completed
    ? ["Candidate", "Job Role", "Conducted On", "Duration", "Feedback Submitted", "Status", "Action"]
    : ["Candidate", "Job Role", "Conducted On", "Duration", "Status", "Action"];
  return (
    <TableContainer sx={{ height: LEDGER_HEIGHT, border: "1px solid #eceff4", borderRadius: "8px" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ height: LEDGER_HEADER_HEIGHT }}>
            {columns.map((column) => (
              <TableCell key={column} className="tableHeader" align={column === "Action" ? "right" : "left"} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.interview_id} hover sx={{ height: LEDGER_ROW_HEIGHT, "& td": { py: 1 } }}>
              {/* Candidate */}
              <TableCell sx={{ maxWidth: 260 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                  <Avatar sx={{ ...commonAvatarStyle, width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>{initials(item.candidate_name)}</Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <TruncatedText onlyWhenTruncated text={item.candidate_name} sx={{ fontWeight: 600, fontSize: "14px" }} />
                    <TruncatedText onlyWhenTruncated text={item.candidate_email} sx={{ fontSize: "12px", color: "text.secondary" }} />
                  </Box>
                </Box>
              </TableCell>
              {/* Job role */}
              <TableCell sx={{ maxWidth: 220 }}>
                <TruncatedText onlyWhenTruncated text={item.job_role || "-"} sx={{ fontSize: "13px", color: "#1e88e5", fontWeight: 500 }} />
              </TableCell>
              <DateCell value={item.conducted_at} />
              {/* Duration */}
              <TableCell sx={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                {item.duration_minutes ? `${item.duration_minutes} Minutes` : "-"}
              </TableCell>
              {completed && <DateCell value={item.feedback_updated_at} />}
              {/* Status */}
              <TableCell>
                <Chip
                  label={completed ? "Feedback submitted" : "Feedback due"}
                  size="small"
                  color={completed ? "success" : "warning"}
                  variant="outlined"
                  sx={{ height: 22, fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap" }}
                />
              </TableCell>
              {/* Action */}
              <TableCell align="right">
                <Button
                  size="small"
                  variant={completed ? "outlined" : "contained"}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
                  onClick={() => onOpen(item)}
                  sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  {completed ? "View feedback" : "Submit feedback"}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Feedback Ledger: Pending feedback (GET /pending-feedback) · Completed feedback (GET /completed-feedback)
const FeedbackLedger = ({ onViewAll, onOpenFeedback }) => {
  const [tab, setTab] = useState(0);
  const pending = useFeedbackList("/pending-feedback", "conducted_at");
  const completed = useFeedbackList("/completed-feedback", "feedback_updated_at");
  const current = tab === 0 ? pending : completed;
  return (
    <Card sx={{ ...cardSx, p: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderBottom: "1px solid #eceff4", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "18px" }}>
            Feedback Ledger
          </Typography>
          <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 } }}>
            <Tab label="Pending feedback" />
            <Tab label="Completed feedback" />
          </Tabs>
        </Box>
        <Button size="small" endIcon={<ArrowForwardIcon />} onClick={onViewAll} sx={{ textTransform: "none", fontWeight: 600 }}>
          View all interviews
        </Button>
      </Box>
      <FeedbackTable data={current.interviews} loading={current.loading} completed={tab === 1} onOpen={onOpenFeedback} />
    </Card>
  );
};

/* ---------------- Data hooks ---------------- */

// GET /pending-feedback or /completed-feedback: latest first by `sortKey`
const useFeedbackList = (path, sortKey) => {
  const { showSnackbar } = useSnackbar();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    const fetchFeedback = async () => {
      setLoading(true);
      try {
        const res = await api.get(path);
        if (ignore) return;
        const list = res?.data?.status === "success" ? res.data.data?.interviews || [] : [];
        setInterviews([...list].sort((x, y) => dayjs(y[sortKey]).diff(dayjs(x[sortKey]))));
      } catch (error) {
        if (!ignore) {
          setInterviews([]);
          showApiError(
            showSnackbar,
            error,
            `Failed to load ${path === "/completed-feedback" ? "completed" : "pending"} feedback. Please try again.`
          );
        }
        console.log("Error : ", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchFeedback();
    return () => {
      ignore = true;
    };
  }, [path, sortKey, showSnackbar]);

  return { interviews, loading };
};

/* ---------------- Page ---------------- */

const CorporateInterviewerDashboard = () => {
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Same summary API as HR Admin / Hiring Manager; the backend returns the interviewer's figures.
  // KPI cards: all-time figures (no year/month sent)
  const { summary, loading: summaryLoading } = useDashboardSummary(null);
  // Interviews by Status: month picked in that card's filter
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0].format("YYYY-MM"));
  const { interviewsByStatus, loading: statusLoading } = useDashboardSummary(selectedMonth);
  // Same /upcoming-interviews API as the other corporate dashboards (from now on, soonest first)
  const { interviews, loading: interviewsLoading } = useUpcomingInterviews();
  const upcomingInterviews = interviews.filter((item) => ACTIVE_STATUSES.includes(String(item.status).toUpperCase()));

  // Next interview later today (for the Today card caption)
  const nextToday = upcomingInterviews.find(
    (item) => dayjs(item.scheduled_at).isSame(dayjs(), "day") && dayjs(item.scheduled_at).isAfter(dayjs())
  );
  const openInterviewList = () => navigate("/interview-schedule-list");
  // Same feedback page the Interview Schedules list opens
  const openFeedback = (item) =>
    navigate("/interview-feedback", { state: { interviewId: item.interview_id, interviewStatus: item.interview_status } });
  // Same page the Interview Schedules list opens for a row
  const openInterview = (item) =>
    navigate("/interview-feedback", { state: { interviewId: item.interview_id, interviewStatus: item.status } });

  return (
    <Box>
      <DashboardHeader />

      {/* KPI cards */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "stretch", flexWrap: "wrap", gap: 1, mb: 3 }}>
        <KpiCard
          show={loaded}
          delay={300}
          loading={summaryLoading || interviewsLoading}
          label="Today"
          value={summary.interviews_today}
          caption={nextToday ? `Next at ${dayjs(nextToday.scheduled_at).format("hh:mm A")}` : "No more interviews today"}
          image={interviewIcon}
        />
        <KpiCard
          show={loaded}
          delay={400}
          loading={summaryLoading}
          label="Total Interviews"
          value={summary.total_interviews_this_month}
          image={examTime}
        />
        <KpiCard
          show={loaded}
          delay={500}
          loading={summaryLoading}
          label="Completed Interviews"
          value={summary.completed_interviews_this_month}
          image={exam}
        />
        <KpiCard
          show={loaded}
          delay={600}
          loading={summaryLoading}
          label="Declined Invitations"
          value={summary.declined_interviews_this_month}
          image={test}
        />
      </Box>

      {/* Upcoming interviews + interviews by status */}
      <SectionTitle icon={<EventNoteOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />} title="Interview Overview" />
      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 3, mb: 3 }}>
        <Box sx={{ flex: { lg: "1.4 1 0" }, minWidth: 0, display: "flex" }}>
          <UpcomingInterviews
            data={upcomingInterviews}
            loading={interviewsLoading}
            emptyDescription="You have no scheduled or rescheduled interviews coming up."
            footerLabel="View my interviews"
            // About 3 interview rows visible (same height as Interviews by Status); the rest scroll
            listMinHeight={300}
            onOpenScheduler={openInterviewList}
            onOpenInterview={openInterview}
          />
        </Box>
        <InterviewsByStatus
          large
          data={interviewsByStatus}
          loading={statusLoading}
          period={dayjs(`${selectedMonth}-01`).format("MMM YYYY")}
          headerAction={<MonthFilter value={selectedMonth} onChange={setSelectedMonth} />}
        />
      </Box>

      {/* Feedback */}
      <SectionTitle icon={<RateReviewOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />} title="Feedback" />
      <FeedbackLedger onViewAll={openInterviewList} onOpenFeedback={openFeedback} />
    </Box>
  );
};

export default CorporateInterviewerDashboard;
